import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RouteTarget, UrlMapDetails } from './lbRoutingChart';

function targetLabel(t?: RouteTarget): string | undefined {
  if (!t) return undefined;
  if (t.kind === 'service') return t.service;
  if (t.kind === 'weighted') return t.backends.map(b => b.service).join(', ');
  return `redirect → ${t.hostRedirect ?? ''}${t.pathRedirect ?? ''}`;
}

export function LoadBalancerCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  if (annotations['gcp.com/service'] !== 'gcp-lb') {
    return null;
  }

  const assetType = annotations['gcp.com/asset-type'] ?? '';
  const details = (entity.spec?.gcpDetails ?? undefined) as
    Record<string, unknown> | undefined;

  if (assetType === 'compute.googleapis.com/UrlMap') {
    const urlMap = (details ?? {}) as unknown as UrlMapDetails;
    const matchers = urlMap.pathMatchers ?? [];
    const hosts = [...new Set(matchers.flatMap(pm => pm.hosts ?? []))];
    const routeCount = matchers.reduce(
      (n, pm) => n + (pm.routes?.length ?? 0),
      0,
    );
    const defaultLabel = targetLabel(urlMap.default);

    return (
      <InfoCard title="Load Balancer">
        <Box>
          {defaultLabel && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Default: <strong>{defaultLabel}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {matchers.length} path matcher(s), {routeCount} route rule(s)
          </Typography>
          {hosts.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Hosts
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {hosts.map(host => (
                  <Chip
                    key={host}
                    label={host}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </InfoCard>
    );
  }

  if (assetType === 'compute.googleapis.com/BackendService') {
    const { protocol, backendCount, healthCheck } = (details ?? {}) as {
      protocol?: string;
      backendCount?: number;
      healthCheck?: string;
    };

    return (
      <InfoCard title="Load Balancer">
        <Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {protocol && (
              <Chip label={protocol} size="small" variant="outlined" />
            )}
            {backendCount !== undefined && (
              <Chip
                label={`Backends: ${backendCount}`}
                size="small"
                variant="outlined"
              />
            )}
            {healthCheck && (
              <Chip
                label={`Health check: ${healthCheck}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </InfoCard>
    );
  }

  if (assetType === 'compute.googleapis.com/ForwardingRule') {
    const { ipAddress, ipProtocol, portRange, loadBalancingScheme } =
      (details ?? {}) as {
        ipAddress?: string;
        ipProtocol?: string;
        portRange?: string;
        loadBalancingScheme?: string;
      };

    return (
      <InfoCard title="Load Balancer">
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {ipAddress && (
            <Chip label={ipAddress} size="small" variant="outlined" />
          )}
          {ipProtocol && (
            <Chip label={ipProtocol} size="small" variant="outlined" />
          )}
          {portRange && (
            <Chip
              label={`Ports: ${portRange}`}
              size="small"
              variant="outlined"
            />
          )}
          {loadBalancingScheme && (
            <Chip label={loadBalancingScheme} size="small" variant="outlined" />
          )}
        </Box>
      </InfoCard>
    );
  }

  if (
    assetType === 'compute.googleapis.com/TargetHttpProxy' ||
    assetType === 'compute.googleapis.com/TargetHttpsProxy'
  ) {
    const { urlMap, sslCertificateCount } = (details ?? {}) as {
      urlMap?: string;
      sslCertificateCount?: number;
    };

    return (
      <InfoCard title="Load Balancer">
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {urlMap && (
            <Chip
              label={`URL map: ${urlMap}`}
              size="small"
              variant="outlined"
            />
          )}
          {sslCertificateCount !== undefined && (
            <Chip
              label={`SSL certs: ${sslCertificateCount}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </InfoCard>
    );
  }

  if (assetType === 'compute.googleapis.com/BackendBucket') {
    const { bucketName, enableCdn } = (details ?? {}) as {
      bucketName?: string;
      enableCdn?: boolean;
    };

    return (
      <InfoCard title="Load Balancer">
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {bucketName && (
            <Chip
              label={`Bucket: ${bucketName}`}
              size="small"
              variant="outlined"
            />
          )}
          {enableCdn !== undefined && (
            <Chip
              label={`CDN: ${enableCdn ? 'on' : 'off'}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </InfoCard>
    );
  }

  // Unknown LB sub-type — show generic card
  return (
    <InfoCard title="Load Balancer">
      <Box>
        <Typography variant="body2" color="text.secondary">
          {assetType || 'Unknown asset type'}
        </Typography>
      </Box>
    </InfoCard>
  );
}
